import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../state/dashboardStore';
import type { Dashboard } from '../lib/dashboards';

const RELATIVE_UNITS: Array<{ limit: number; divisor: number; unit: string }> = [
  { limit: 60_000, divisor: 1_000, unit: 'second' },
  { limit: 3_600_000, divisor: 60_000, unit: 'minute' },
  { limit: 86_400_000, divisor: 3_600_000, unit: 'hour' },
  { limit: 604_800_000, divisor: 86_400_000, unit: 'day' },
  { limit: 2_629_800_000, divisor: 604_800_000, unit: 'week' },
  { limit: 31_557_600_000, divisor: 2_629_800_000, unit: 'month' },
];

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  if (diff < 0) return 'just now';
  for (const { limit, divisor, unit } of RELATIVE_UNITS) {
    if (diff < limit) {
      const n = Math.max(1, Math.floor(diff / divisor));
      return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
    }
  }
  const years = Math.floor(diff / 31_557_600_000);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function nextDefaultName(existing: Dashboard[]): string {
  let n = existing.length + 1;
  const taken = new Set(existing.map((d) => d.name));
  while (taken.has(`Dashboard ${n}`)) n++;
  return `Dashboard ${n}`;
}

type TileProps = {
  dashboard: Dashboard;
  onOpen: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
};

function DashboardTile({ dashboard, onOpen, onRename, onDelete }: TileProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(dashboard.name);

  const filterCount = dashboard.filterStages.length;
  const widgetCount = dashboard.widgets.length;

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== dashboard.name) {
      onRename(dashboard.id, trimmed);
    } else {
      setDraftName(dashboard.name);
    }
    setEditing(false);
  };

  return (
    <li className="dashboard-tile">
      <button
        type="button"
        className="dashboard-tile__open"
        onClick={() => onOpen(dashboard.id)}
        aria-label={`Open dashboard ${dashboard.name}`}
      >
        <div className="dashboard-tile__meta">
          {filterCount} filter{filterCount === 1 ? '' : 's'} ·{' '}
          {widgetCount} widget{widgetCount === 1 ? '' : 's'}
        </div>
        <div className="dashboard-tile__created">
          Created {relativeTime(dashboard.createdAt)}
        </div>
      </button>
      <div className="dashboard-tile__footer">
        {editing ? (
          <input
            className="dashboard-tile__name-input"
            value={draftName}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') {
                setDraftName(dashboard.name);
                setEditing(false);
              }
            }}
            aria-label="Dashboard name"
          />
        ) : (
          <span
            className="dashboard-tile__name"
            title={dashboard.name}
          >
            {dashboard.name}
          </span>
        )}
        <div className="dashboard-tile__actions">
          <button
            type="button"
            className="dashboard-tile__action"
            onClick={() => {
              setDraftName(dashboard.name);
              setEditing(true);
            }}
            aria-label={`Rename ${dashboard.name}`}
          >
            Rename
          </button>
          <button
            type="button"
            className="dashboard-tile__action dashboard-tile__action--danger"
            onClick={() => {
              if (confirm(`Delete dashboard "${dashboard.name}"?`)) {
                onDelete(dashboard.id);
              }
            }}
            aria-label={`Delete ${dashboard.name}`}
          >
            Delete
          </button>
        </div>
      </div>
    </li>
  );
}

export default function DashboardsListPage() {
  const dashboards = useDashboardStore((s) => s.dashboards);
  const createDashboard = useDashboardStore((s) => s.createDashboard);
  const renameDashboard = useDashboardStore((s) => s.renameDashboard);
  const deleteDashboard = useDashboardStore((s) => s.deleteDashboard);
  const navigate = useNavigate();

  const sorted = useMemo(
    () =>
      [...dashboards].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [dashboards],
  );

  const handleCreate = () => {
    const name = nextDefaultName(dashboards);
    const created = createDashboard(name, {
      stages: [],
      baseSelection: null,
      polygon: null,
    });
    navigate(`/dashboards/${created.id}`, { state: { justCreated: true } });
  };

  return (
    <div className="dashboards-page">
      <header className="dashboards-page__header">
        <h1 className="dashboards-page__title">Dashboards</h1>
        <p className="dashboards-page__subtitle">
          Snapshot a filter from the map and analyse it on its own canvas.
        </p>
      </header>
      {dashboards.length === 0 ? (
        <div className="dashboards-empty">
          <h2 className="dashboards-empty__title">No dashboards yet</h2>
          <p className="dashboards-empty__body">
            Create your first dashboard from the map view's Filter panel, or
            start a blank one here.
          </p>
          <button
            type="button"
            className="dashboards-empty__cta"
            onClick={handleCreate}
          >
            Create blank dashboard
          </button>
        </div>
      ) : (
        <ul className="dashboards-grid">
          <li className="dashboards-grid__new">
            <button
              type="button"
              className="dashboards-grid__new-button"
              onClick={handleCreate}
            >
              <span className="dashboards-grid__new-plus" aria-hidden="true">
                +
              </span>
              <span>New dashboard</span>
              <span className="dashboards-grid__new-hint">Blank canvas</span>
            </button>
          </li>
          {sorted.map((d) => (
            <DashboardTile
              key={d.id}
              dashboard={d}
              onOpen={(id) => navigate(`/dashboards/${id}`)}
              onRename={renameDashboard}
              onDelete={deleteDashboard}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
